import { shiftDays } from '@/lib/asof/asof';
import type { RangeKey } from '@/stores/chartPrefsStore';

/**
 * =============================================================================
 *  範圍藥丸（1M 3M 6M 1Y 3Y 5Y ALL）→ 具體日期窗口。
 *
 *  **右端永遠是 `as_of`，不是瀏覽器的今天。** 倒帶到 2015-06-30 時按「1Y」，
 *  要的是 2014-06-30 到 2015-06-30，不是「最近一年然後被後端砍掉一半」。
 *  這一行寫錯的症狀是：倒帶之後圖表看起來正常，但範圍藥丸全部失效——
 *  每一個都畫出同一段（as_of 之前那一小截），而使用者只會覺得「怪怪的」。
 * ========================================================================== */

/** 專案的資料起點。ALL 用它當左端，而不是用一個沒有意義的 1900。 */
export const DATA_START = '1990-01-01';

const DAYS: Record<Exclude<RangeKey, 'ALL'>, number> = {
  '1M': 31,
  '3M': 92,
  '6M': 183,
  '1Y': 366,
  '3Y': 1096,
  '5Y': 1827,
};

export interface DateWindow {
  from: string;
  to: string;
}

export function rangeToWindow(range: RangeKey, anchor: string): DateWindow {
  if (range === 'ALL') return { from: DATA_START, to: anchor };
  return { from: shiftDays(anchor, -DAYS[range]), to: anchor };
}

/**
 * 把一條自己有日期軸的序列，對齊到 K 線的交易日軸。
 *
 * 為什麼需要它：籌碼 pane（外資買賣超）來自 `/series/{metric_key}`，
 * 那個端點回的是**它自己的**日期（有公布落差、有週更的 metric），
 * 而 lightweight-charts 的 `ChipOverlay.values` 契約要求「與 `data.t` 等長且同序」。
 * 兩邊長度不一致時，histogram 會整條往左偏移幾根——那是一個看起來
 * 「資料好像怪怪的」但沒有人指得出哪裡錯的 bug。
 *
 * 對不上的交易日補 `null`（畫成缺口），**不是補 0**：0 的意思是「當天買賣超為零」，
 * 缺口的意思是「當天沒有這個數字」。在籌碼圖上這兩件事差很多。
 */
export function alignToCalendar(
  calendar: readonly string[],
  seriesDates: readonly string[],
  seriesValues: readonly (number | null)[],
): (number | null)[] {
  const byDate = new Map<string, number | null>();
  for (let i = 0; i < seriesDates.length; i += 1) {
    byDate.set(seriesDates[i], seriesValues[i] ?? null);
  }
  return calendar.map((day) => byDate.get(day) ?? null);
}
